/* eslint-disable react-hooks/exhaustive-deps */
import {
  ChangeEventHandler,
  FC,
  FormEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { IoKeyOutline } from 'react-icons/io5';
import { FiUser } from 'react-icons/fi';

import { useTheme } from 'styled-components';

import {
  BUSINESS_BLOCKED_MESSAGE,
  isBusinessBlockedError,
} from '~/common/utils/business-blocked';
import { IAuthLogin } from '~/common/services/controllers/auth';
import { useToast } from '~/common/providers/toast';
import { useBusiness } from '~/common/providers/business';
import { useAuth } from '~/common/providers/auth';
import { Typography } from '~/common/components/Typography';
import { TextInput } from '~/common/components/TextInput';
import { Button } from '~/common/components/Button';

import { StyContainer, StyHeader } from './styles';

export const LoginPage: FC = () => {
  const theme = useTheme();
  const { login: tryLogin } = useAuth();
  const { business } = useBusiness();
  const { showToast } = useToast();
  const [login, setLogin] = useState<IAuthLogin>({
    business: 'monteiro',
  } as IAuthLogin);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    setLogin((prevLogin) => ({
      ...prevLogin,
      business: business?.slug || 'monteiro',
    }));
  }, [business?.slug]);

  const handleLoginSubmit: FormEventHandler = useCallback(
    async (event) => {
      event.preventDefault();
      try {
        setLoading(true);
        await tryLogin(login);
      } catch (error) {
        if (isBusinessBlockedError(error)) {
          showToast({
            severity: 'warning',
            message: error?.data?.message || BUSINESS_BLOCKED_MESSAGE,
          });
          return;
        }

        const message: string =
          error?.data?.message ||
          'Erro ao tentar realizar o login. Tente novamente.';
        showToast({
          severity: 'error',
          message,
        });
      } finally {
        setLoading(false);
      }
    },
    [login, tryLogin, showToast]
  );

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const { name, value } = event.target;
      setLogin((prevLogin) => ({
        ...prevLogin,
        [name]: value,
      }));
    },
    []
  );

  const allowPasswordAutofillForAdmin =
    login.login?.trim().toLowerCase() === 'admin';

  return (
    <StyContainer
      onSubmit={handleLoginSubmit}
      autoComplete={allowPasswordAutofillForAdmin ? 'on' : 'off'}
    >
      <StyHeader>
        <Typography.Title size="h5">Bem-vindo de volta</Typography.Title>
        <Typography.Label size="small" color="neutral.600">
          Acesse sua conta para continuar
        </Typography.Label>
      </StyHeader>
      <TextInput
        name="login"
        width="100%"
        leftIcon={FiUser}
        value={login.login}
        onChange={handleInputChange}
        autoComplete={allowPasswordAutofillForAdmin ? 'username' : 'off'}
        placeholder="Digite seu login/nome de usuário"
        required
        label={{
          label: 'Login',
          isRequired: true,
        }}
      />
      <TextInput
        type="password"
        name="password"
        width="100%"
        required
        leftIcon={IoKeyOutline}
        value={login.password}
        onChange={handleInputChange}
        placeholder="Digite sua senha"
        autoComplete={
          allowPasswordAutofillForAdmin ? 'current-password' : 'new-password'
        }
        label={{
          label: 'Senha',
          isRequired: true,
        }}
      />

      <Button
        type="submit"
        text="Entrar"
        width="100%"
        backgroundColor={theme.primary}
        textOrIconColorOnHover={theme.primary}
        textOrIconColor="white"
        withArrowRight
        loading={loading}
        loader="bar"
        loaderColor="primary"
      />
    </StyContainer>
  );
};
